import React from 'react';
import {
  Box,
  Typography,
  Avatar,
  Chip,
  useTheme,
  Paper,
} from '@mui/material';
import { AISelectorProps, EXPERT_AVATARS } from '../../types/ai';

/**
 * 現在選択されている専門家AI表示コンポーネント
 * 専門家の切り替えは行わず、現在の専門家の情報のみを表示
 */
export const AISelector: React.FC<AISelectorProps> = ({
  selectedExpert,
  isLoading = false,
  disabled = false,
}) => {
  const theme = useTheme();

  // 専門家の名前から種類を推測してアバター絵文字を取得
  const getExpertAvatar = (name: string): string => {
    const lowercaseName = name.toLowerCase();
    
    if (lowercaseName.includes('不動産') || lowercaseName.includes('営業')) {
      return EXPERT_AVATARS.real_estate_sales;
    } else if (lowercaseName.includes('法務') || lowercaseName.includes('法律')) {
      return EXPERT_AVATARS.legal;
    } else if (lowercaseName.includes('税務') || lowercaseName.includes('税金')) {
      return EXPERT_AVATARS.tax;
    }
    return EXPERT_AVATARS.default;
  };

  // 専門家が選択されていない場合
  if (!selectedExpert) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 2,
          backgroundColor: theme.palette.grey[50],
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
        }}
      >
        <Typography
          variant="body2"
          color="textSecondary"
          sx={{ textAlign: 'center' }}
        >
          専門家AIが選択されていません
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 1,
        opacity: disabled || isLoading ? 0.6 : 1,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Avatar
          sx={{
            width: 48,
            height: 48,
            fontSize: '1.25rem',
            backgroundColor: theme.palette.secondary.light,
            color: theme.palette.secondary.contrastText,
          }}
        >
          {getExpertAvatar(selectedExpert.name)}
        </Avatar>

        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {selectedExpert.name}
            </Typography>
          </Box>
          
          <Typography
            variant="body2"
            color="textSecondary"
            sx={{ lineHeight: 1.4 }}
          >
            {selectedExpert.description}
          </Typography>
        </Box>

        {/* ローディング状態の表示 */}
        {isLoading && (
          <Typography variant="caption" color="textSecondary">
            読み込み中...
          </Typography>
        )}
      </Box>

      {/* 専門家変更のヒント */}
      <Box
        sx={{
          mt: 1.5,
          pt: 1.5,
          borderTop: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Typography
          variant="caption"
          color="textSecondary"
          sx={{
            display: 'block',
            textAlign: 'center',
            fontStyle: 'italic',
          }}
        >
          💡 別の専門家に相談したい場合は、サイドバーの「新しいチャット」をクリックしてください
        </Typography>
      </Box>
    </Paper>
  );
};